import { GetAccountsOptions } from 'types/account'
import { GetCustomersOptions } from 'types/customers'
import { GetProductsOptions } from 'types/product'
import { GetPurchasesOptions } from 'types/purchase'
import { GetTransactionsOptions } from 'types/transaction'
import { FormConfigQuery, BusinessType } from 'types/form-config.types'
import {
  AccountSearchOptions,
  AuditLogFilters,
  OperationEnvironment,
  PreregistrationFilters,
  ProviderListOptions,
} from 'types/operation'

const normalizeAccountsOptions = (options?: GetAccountsOptions): GetAccountsOptions => ({
  entityType: options?.entityType,
  includeTransactions: options?.includeTransactions,
  includePurchases: options?.includePurchases,
  transactionLimit: options?.transactionLimit,
  purchaseLimit: options?.purchaseLimit,
})

const normalizeProductsOptions = (options?: GetProductsOptions): GetProductsOptions => ({
  limit: options?.limit,
  offset: options?.offset,
  search: options?.search,
  status: options?.status,
  isMcpPay: options?.isMcpPay,
  productType: options?.productType,
})

const normalizeCustomersOptions = (options?: GetCustomersOptions): GetCustomersOptions => ({
  limit: options?.limit,
  offset: options?.offset,
  search: options?.search,
  status: options?.status,
  planId: options?.planId,
})

const normalizePurchasesOptions = (options?: GetPurchasesOptions) => ({
  status: options?.status ?? undefined,
  productId: options?.productId ?? undefined,
  customerId: options?.customerId ?? undefined,
  planId: options?.planId ?? undefined,
  includeFree: options?.includeFree ?? undefined,
  minAmount: options?.minAmount ?? undefined,
  maxAmount: options?.maxAmount ?? undefined,
  startDateFrom: options?.startDateFrom ?? undefined,
  startDateTo: options?.startDateTo ?? undefined,
})

const normalizeTransactionsOptions = (
  options?: GetTransactionsOptions,
): GetTransactionsOptions => ({
  limit: options?.limit,
  offset: options?.offset,
  status: options?.status,
  type: options?.type,
  includeStats: options?.includeStats,
  productId: options?.productId,
  entityId: options?.entityId,
  fromDate: options?.fromDate,
  toDate: options?.toDate,
})

export const accountsKeys = {
  all: () => ['accounts'] as const,
  list: (options?: GetAccountsOptions) => ['accounts', normalizeAccountsOptions(options)] as const,
}

export const mcpServersKeys = {
  all: () => ['mcpServers'] as const,
  detail: (id: string) => ['mcpServers', 'detail', id] as const,
}

export const mcpCustomDomainKeys = {
  detail: (serverId: string) => ['mcpServers', 'customDomain', serverId] as const,
}

export const consentKeys = {
  all: () => ['consent'] as const,
  status: () => ['consent', 'status'] as const,
  documents: () => ['consent', 'documents'] as const,
}

export const providersKeys = {
  all: () => ['providers'] as const,
  current: (userId?: string) =>
    userId ? (['providers', 'current', userId] as const) : (['providers', 'current'] as const),
}

export const productsKeys = {
  all: () => ['products'] as const,
  list: (options?: GetProductsOptions) => ['products', normalizeProductsOptions(options)] as const,
  detail: (id: string) => ['products', 'detail', id] as const,
  stats: () => ['products', 'stats'] as const,
}

export const mcpServerStatsKeys = {
  detail: (id: string) => ['mcpServers', 'stats', id] as const,
}

export const userKeys = {
  me: () => ['user', 'me'] as const,
  authCapabilities: () => ['user', 'authCapabilities'] as const,
}

export const customersKeys = {
  all: () => ['customers'] as const,
  list: (options?: GetCustomersOptions) =>
    ['customers', normalizeCustomersOptions(options)] as const,
  detail: (id: string) => ['customers', 'detail', id] as const,
  stats: () => ['customers', 'stats'] as const,
}

export const purchasesKeys = {
  all: () => ['purchases'] as const,
  list: (options?: GetPurchasesOptions) =>
    ['purchases', normalizePurchasesOptions(options)] as const,
  detail: (id: string) => ['purchases', 'detail', id] as const,
}

export const plansKeys = {
  all: () => ['plans'] as const,
  forProduct: (productRef: string) => ['plans', 'product', productRef] as const,
  detail: (ref: string) => ['plans', 'detail', ref] as const,
}

export const transactionsKeys = {
  all: () => ['transactions'] as const,
  list: (options?: GetTransactionsOptions) =>
    ['transactions', normalizeTransactionsOptions(options)] as const,
  detail: (id: string) => ['transactions', 'detail', id] as const,
  events: (id: string) => ['transactions', 'events', id] as const,
}

export const payoutKeys = {
  all: () => ['payouts'] as const,
  settings: () => ['payouts', 'settings'] as const,
  history: (limit?: number, source: 'solvapay' | 'all' = 'solvapay') =>
    ['payouts', 'history', limit ?? 20, source] as const,
}

export const providerHomeKeys = {
  transactions: (providerId?: string) =>
    ['providerHome', 'transactions', providerId ?? 'unknown'] as const,
  purchases: (providerId?: string) =>
    ['providerHome', 'purchases', providerId ?? 'unknown'] as const,
  products: (providerId?: string) => ['providerHome', 'products', providerId ?? 'unknown'] as const,
  customers: (providerId?: string) =>
    ['providerHome', 'customers', providerId ?? 'unknown'] as const,
  grossVolumeStats: (days: number) => ['providerHome', 'grossVolumeStats', days] as const,
  netVolumeStats: (days: number) => ['providerHome', 'netVolumeStats', days] as const,
  topCustomers: (days: number) => ['providerHome', 'topCustomers', days] as const,
  failedTransactionsStats: (days: number) =>
    ['providerHome', 'failedTransactionsStats', days] as const,
  newCustomersStats: (days: number) => ['providerHome', 'newCustomersStats', days] as const,
  usageOverallStats: (days: number) => ['providerHome', 'usageOverallStats', days] as const,
  goLiveChecklist: (options?: { method?: 'oauth' | 'new'; stripeAccountId?: string }) =>
    ['providerHome', 'goLiveChecklist', options ?? {}] as const,
}

export const usageKeys = {
  all: () => ['usage'] as const,
  overallStats: (days: number) => ['usage', 'overall', days] as const,
  byProduct: (days: number) => ['usage', 'byProduct', days] as const,
  byMcp: (days: number) => ['usage', 'byMcp', days] as const,
  byCustomer: (days: number) => ['usage', 'byCustomer', days] as const,
  byPlan: (days: number) => ['usage', 'byPlan', days] as const,
}

export const meterKeys = {
  all: () => ['meters'] as const,
  list: (status?: string) => ['meters', 'list', status] as const,
}

export const creditsKeys = {
  all: () => ['credits'] as const,
  balance: (customerId: string) => ['credits', 'balance', customerId] as const,
  providerBalances: () => ['credits', 'providerBalances'] as const,
  ledger: (options?: Record<string, unknown>) => ['credits', 'ledger', options ?? {}] as const,
  customerLedger: (customerId: string, options?: Record<string, unknown>) =>
    ['credits', 'ledger', customerId, options ?? {}] as const,
}

export const financialsKeys = {
  all: () => ['financials'] as const,
  pnl: (period: string) => ['financials', 'pnl', period] as const,
  cashflow: (period: string) => ['financials', 'cashflow', period] as const,
  products: (period: string) => ['financials', 'products', period] as const,
  ledger: (period: string, options?: Record<string, unknown>) =>
    ['financials', 'ledger', period, options] as const,
}

// --- Admin query keys ------------------------------------------------------

export const adminTransactionsKeys = {
  all: () => ['admin', 'transactions'] as const,
  list: (params?: Record<string, unknown>) => ['admin', 'transactions', params] as const,
  stats: () => ['admin', 'transactions', 'stats'] as const,
}

export const adminUsersKeys = {
  all: () => ['admin', 'users'] as const,
  list: (params?: Record<string, unknown>) => ['admin', 'users', params] as const,
  stats: () => ['admin', 'users', 'stats'] as const,
}

export const adminProvidersKeys = {
  all: () => ['admin', 'providers'] as const,
  list: (params?: Record<string, unknown>) => ['admin', 'providers', params] as const,
  stats: () => ['admin', 'providers', 'stats'] as const,
}

export const adminProductsKeys = {
  all: () => ['admin', 'products'] as const,
  list: (params?: Record<string, unknown>) => ['admin', 'products', params] as const,
}

// --- Notification query keys -----------------------------------------------

export const notificationsKeys = {
  all: () => ['notifications'] as const,
  list: (params?: Record<string, unknown>) => ['notifications', params] as const,
  stats: () => ['notifications', 'stats'] as const,
  unreadCount: () => ['notifications', 'unreadCount'] as const,
}

export const formConfigKeys = {
  all: () => ['formConfigs'] as const,
  detail: (query: FormConfigQuery | null) => ['formConfigs', 'detail', query ?? {}] as const,
  wizardSequence: (businessType?: BusinessType) =>
    ['formConfigs', 'wizardSequence', businessType ?? 'none'] as const,
}

// --- Operations (super-admin) query keys ------------------------------------
//
// Environment-scoped resources embed the `environment` in the key so toggling
// sandbox/live refetches. Environment-independent resources (accounts, audit
// log, preregistrations, metrics, recent activity) omit it.

const normalizeAccountSearchOptions = (options?: AccountSearchOptions): AccountSearchOptions => ({
  q: options?.q,
  type: options?.type,
  page: options?.page,
  limit: options?.limit,
})

const normalizeProviderListOptions = (options?: ProviderListOptions): ProviderListOptions => ({
  search: options?.search,
  status: options?.status,
  limit: options?.limit,
  offset: options?.offset,
})

const normalizeAuditLogFilters = (filters?: AuditLogFilters): AuditLogFilters => ({
  actorId: filters?.actorId,
  action: filters?.action,
  targetId: filters?.targetId,
  targetType: filters?.targetType,
  startDate: filters?.startDate,
  endDate: filters?.endDate,
  page: filters?.page,
  limit: filters?.limit,
})

const normalizePreregistrationFilters = (
  filters?: PreregistrationFilters,
): PreregistrationFilters => ({
  q: filters?.q,
  status: filters?.status,
  page: filters?.page,
  limit: filters?.limit,
})

export const operationKeys = {
  all: () => ['operation'] as const,

  dashboard: {
    all: () => ['operation', 'dashboard'] as const,
    metrics: (env: OperationEnvironment) => ['operation', 'dashboard', 'metrics', env] as const,
    attention: (env: OperationEnvironment) => ['operation', 'dashboard', 'attention', env] as const,
    transactions: (env: OperationEnvironment) =>
      ['operation', 'dashboard', 'transactions', env] as const,
    growth: (env: OperationEnvironment) => ['operation', 'dashboard', 'growth', env] as const,
    activity: () => ['operation', 'dashboard', 'activity'] as const,
  },

  accounts: {
    all: () => ['operation', 'accounts'] as const,
    list: (options?: AccountSearchOptions) =>
      ['operation', 'accounts', 'list', normalizeAccountSearchOptions(options)] as const,
    detail: (id: string) => ['operation', 'accounts', 'detail', id] as const,
  },

  providers: {
    all: () => ['operation', 'providers'] as const,
    list: (env: OperationEnvironment, options?: ProviderListOptions) =>
      ['operation', 'providers', 'list', env, normalizeProviderListOptions(options)] as const,
    detail: (env: OperationEnvironment, id: string) =>
      ['operation', 'providers', 'detail', env, id] as const,
    products: (env: OperationEnvironment, id: string) =>
      ['operation', 'providers', 'detail', env, id, 'products'] as const,
    purchases: (env: OperationEnvironment, id: string) =>
      ['operation', 'providers', 'detail', env, id, 'purchases'] as const,
    transactions: (env: OperationEnvironment, id: string) =>
      ['operation', 'providers', 'detail', env, id, 'transactions'] as const,
    customerPurchases: (env: OperationEnvironment, id: string, customerId: string) =>
      ['operation', 'providers', 'detail', env, id, 'purchases', 'customer', customerId] as const,
    customerTransactions: (env: OperationEnvironment, id: string, customerId: string) =>
      [
        'operation',
        'providers',
        'detail',
        env,
        id,
        'transactions',
        'customer',
        customerId,
      ] as const,
    customers: (env: OperationEnvironment, id: string) =>
      ['operation', 'providers', 'detail', env, id, 'customers'] as const,
    users: (env: OperationEnvironment, id: string) =>
      ['operation', 'providers', 'detail', env, id, 'users'] as const,
    meters: (env: OperationEnvironment, id: string) =>
      ['operation', 'providers', 'detail', env, id, 'meters'] as const,
    usage: (env: OperationEnvironment, id: string, from: string, to: string) =>
      ['operation', 'providers', 'detail', env, id, 'usage', from, to] as const,
  },

  // Single-entity detail reads (deep-linkable detail pages). Env-scoped because
  // the underlying documents live in per-environment collections.
  entities: {
    product: (env: OperationEnvironment, id: string) =>
      ['operation', 'entity', 'product', env, id] as const,
    customer: (env: OperationEnvironment, id: string) =>
      ['operation', 'entity', 'customer', env, id] as const,
    purchase: (env: OperationEnvironment, id: string) =>
      ['operation', 'entity', 'purchase', env, id] as const,
    meter: (env: OperationEnvironment, id: string) =>
      ['operation', 'entity', 'meter', env, id] as const,
    transaction: (env: OperationEnvironment, id: string) =>
      ['operation', 'entity', 'transaction', env, id] as const,
  },

  preregistrations: {
    all: () => ['operation', 'preregistrations'] as const,
    list: (filters?: PreregistrationFilters) =>
      ['operation', 'preregistrations', 'list', normalizePreregistrationFilters(filters)] as const,
    detail: (id: string) => ['operation', 'preregistrations', 'detail', id] as const,
  },

  auditLog: {
    all: () => ['operation', 'auditLog'] as const,
    list: (filters?: AuditLogFilters) =>
      ['operation', 'auditLog', 'list', normalizeAuditLogFilters(filters)] as const,
  },

  metrics: () => ['operation', 'metrics'] as const,

  // Feature flags are environment-agnostic (release toggles have nothing to do
  // with sandbox/live), so no `env` in the key.
  features: {
    all: () => ['operation', 'features'] as const,
    list: () => ['operation', 'features', 'list'] as const,
    forProvider: (providerId: string) =>
      ['operation', 'features', 'provider', providerId] as const,
  },
}

/**
 * Customer hosted portal (`/customer/manage`) query keys. The portal is
 * session-scoped (no platform user JWT), so every key is namespaced by the
 * opaque `customer-session` id from the URL. These feed the React Query cache
 * that the customer `SocketQueryBridge` invalidates on realtime events.
 */
export const customerSessionKeys = {
  all: (sessionId: string) => ['customerSession', sessionId] as const,
  shell: (sessionId: string) => ['customerSession', sessionId, 'shell'] as const,
  productAccount: (sessionId: string, productRef: string | null | undefined) =>
    ['customerSession', sessionId, 'product-account', productRef ?? null] as const,
  transactions: (sessionId: string) => ['customerSession', sessionId, 'transactions'] as const,
  creditHistory: (sessionId: string) => ['customerSession', sessionId, 'credit-history'] as const,
  availableProducts: (sessionId: string) =>
    ['customerSession', sessionId, 'available-products'] as const,
}
